/**
 * DER MERKPLATZ: WAS EIN BILDSCHIRM BEIM VERLASSEN WEISS, WEISS ER BEIM
 * ZURÜCKKOMMEN NOCH (.398).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (17.10.2026)
 *   „Wenn ich aus der Prüfung von der Gutschrift zur Abrechnung auf die
 *    Adresse klicke, dann gehe ich in Auftrag, gehe dann wieder oben zurück
 *    auf die Abrechnung, dann lädt er aber alles wieder neu, er cached es
 *    nicht. Und dann ist dann nichts mehr markiert, ich bin nicht mehr in dem
 *    Auftrag, in der Adresse drin, muss also dann wieder gucken, wo ich war."
 *
 * React wirft den Zustand eines Bildschirms weg, sobald er vom Schirm geht
 * (die Akte steht an seiner Stelle). Hier liegt er daneben, im Modul, und
 * überlebt den Wechsel: die geladenen Daten, die aufgeklappten Zeilen, der
 * Filter, die Stelle, bis zu der gerollt war.
 *
 * DATEN SIND DABEI NIE ALT GENUG, UM FALSCH ZU WERDEN: `useMerkDaten` zeigt
 * das Gemerkte SOFORT und lädt im Hintergrund frisch nach (der Server hält
 * es ohnehin zwei Minuten vor, siehe api/zwischenspeicher.ts). Nach jedem
 * Schreiben zieht `useFrisch` nach wie bisher.
 *
 * Beim Abmelden wird alles vergessen (`vergissAlles` aus `api.ts`): der
 * nächste Mensch am selben Browser sieht nichts vom vorigen.
 */
import { useEffect, useRef, useState } from 'react';

const platz = new Map<string, unknown>();

export function merke<T>(schluessel: string, wert: T): T { platz.set(schluessel, wert); return wert; }
export function hole<T>(schluessel: string): T | undefined { return platz.get(schluessel) as T | undefined; }
export function vergissAlles(): void { platz.clear(); }

/** Wie `useState`, nur dass der Wert den Bildschirmwechsel überlebt. */
export function useMerkState<T>(schluessel: string, start: T | (() => T)): [T, (w: T | ((alt: T) => T)) => void] {
  const [wert, setWert] = useState<T>(() => {
    const g = hole<T>(schluessel);
    if (g !== undefined) return g;
    return typeof start === 'function' ? (start as () => T)() : start;
  });
  const setze = (w: T | ((alt: T) => T)) => {
    setWert((alt) => {
      const n = typeof w === 'function' ? (w as (alt: T) => T)(alt) : w;
      merke(schluessel, n);
      return n;
    });
  };
  return [wert, setze];
}

/**
 * Daten mit Gedächtnis: beim ersten Öffnen leer und laden; beim Zurückkommen
 * sofort das Gemerkte, dann frisch nachladen. `laden` muss die Daten
 * zurückgeben; was es liefert, wird gemerkt.
 */
export function useMerkDaten<T>(schluessel: string, laden: () => Promise<T>, beiFehler?: (f: unknown) => void):
    [T | null, (n: T | ((alt: T | null) => T | null)) => void, () => Promise<T | null>] {
  const [d, setD] = useState<T | null>(() => hole<T>(schluessel) ?? null);
  const ladenRef = useRef(laden); ladenRef.current = laden;
  const fehlerRef = useRef(beiFehler); fehlerRef.current = beiFehler;
  const setze = (n: T | ((alt: T | null) => T | null)) => {
    setD((alt) => {
      const w = typeof n === 'function' ? (n as (alt: T | null) => T | null)(alt) : n;
      if (w === null) platz.delete(schluessel); else merke(schluessel, w);
      return w;
    });
  };
  const nachladen = async (): Promise<T | null> => {
    try { const n = await ladenRef.current(); merke(schluessel, n); setD(n); return n; }
    catch (f) { fehlerRef.current?.(f); return null; }
  };
  useEffect(() => { nachladen(); }, []);
  return [d, setze, nachladen];
}

/**
 * Die Rollstelle überlebt den Wechsel: beim Verlassen gemerkt, beim
 * Zurückkommen wiederhergestellt, sobald die Daten (wieder) da sind.
 */
/** WAS ROLLT (.479): die Bühne (main.buehne), nicht das Fenster. Bis hierher las und setzte
 *  die Rollstelle window.scrollY, das immer 0 ist; zurück aus der Akte stand man darum oben. */
export function rollbereich(): { lies: () => number; setze: (y: number) => void } {
  const el = typeof document !== 'undefined' ? document.querySelector('main.buehne') as HTMLElement | null : null;
  return el
    ? { lies: () => el.scrollTop, setze: (y) => { el.scrollTop = y; } }
    : { lies: () => window.scrollY, setze: (y) => window.scrollTo({ top: y }) };
}

export function useRollstelle(schluessel: string, bereit: boolean): void {
  const hergestellt = useRef(false);
  useEffect(() => {
    if (!bereit || hergestellt.current) return;
    hergestellt.current = true;
    const y = hole<number>(`roll:${schluessel}`);
    if (y && y > 0) {
      /* Nach dem Zeichnen, sonst ist die Seite noch zu kurz für die Stelle. */
      /* Ein paar Anläufe: Listen, die nachladen, machen die Seite erst nach und nach lang genug. */
      let n = 0;
      const versuch = () => {
        try { const r = rollbereich(); r.setze(y); if (Math.abs(r.lies() - y) > 4 && n++ < 20) setTimeout(versuch, 100); } catch { /* egal */ }
      };
      requestAnimationFrame(() => requestAnimationFrame(versuch));
    }
  }, [bereit]);
  /* Mitschreiben, solange gerollt wird: beim Abbau des Bauteils ist die Bühne schon die nächste Seite. */
  useEffect(() => {
    const r = rollbereich();
    const el = typeof document !== 'undefined' ? (document.querySelector('main.buehne') as HTMLElement | null) : null;
    const ziel: any = el ?? window;
    const mit = () => { try { merke(`roll:${schluessel}`, r.lies()); } catch { /* egal */ } };
    ziel.addEventListener('scroll', mit, { passive: true });
    return () => ziel.removeEventListener('scroll', mit);
  }, []);
}
